import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { type MessageSeen } from '../../server/models/message-seen.model';
import type {
  Channel,
  QueryParams,
  ReqParams,
  ResBody,
} from '../../server/routes/threads/get-thread-channels';
import {
  generatePath,
  sortCaseInsensitive,
} from '../../server/services/utils.service';
import type { Thread } from '../../server/types/message-types';
import { AuthUserService } from './auth-user.service';
import { MessageService } from './message.service';

export type ChannelTitleListItem = {
  _id: string;
  title: string;
  userId: string;
  mode: 'channel';
};

@Injectable({ providedIn: 'root' })
export class ChannelService {
  private http = inject(HttpClient);
  private authUserService = inject(AuthUserService);
  private messageService = inject(MessageService);

  private data:
    | undefined
    | { userId: string; orgId: string; channels: ChannelTitleListItem[] };

  private channelListSubject = new ReplaySubject<ChannelTitleListItem[]>();
  channelList = this.channelListSubject.asObservable();

  private channelSubject = new Subject<{ thread: Thread; seen: MessageSeen }>();
  channel = this.channelSubject.asObservable();

  constructor() {
    this.authUserService.userOrgObj.subscribe((userOrgObj) => {
      if (!userOrgObj) {
        if (!this.data) return;
        this.data = undefined;
        return this.channelListSubject.next([]);
      }

      const orgId = userOrgObj.org._id;
      const userId = userOrgObj.user._id;

      this.data = { orgId, userId, channels: [] };

      // As this is the first load, check local storage first
      const localStorageList = this.getLocalStorageChannelList();
      if (localStorageList) {
        this.data.channels = localStorageList;
        this.channelListSubject.next(localStorageList);
      }

      this.refreshChannelList();
    });

    this.messageService.threadObservable.subscribe((thread) => {
      if (
        this.data &&
        this.data.orgId === thread.orgId &&
        thread.userIds.includes(this.data.userId) &&
        thread.mode === 'channel' &&
        !this.data.channels.find(({ _id }) => _id === thread._id)
      ) {
        const { title, _id, mode, closedAt, createdAt, updatedAt } = thread;
        const channel = { title, _id, mode, closedAt, createdAt, updatedAt };
        const changed = this.handleChannel(channel);

        if (changed) {
          this.sortChannels();
          this.setLocalStorageChannelList();
          this.channelListSubject.next(this.data.channels);
        }
      }
    });
  }

  private refreshChannelList() {
    if (!this.data) return;

    const { orgId, userId } = this.data;
    const params: QueryParams = { userId, closed: '0' };
    const path = generatePath<ReqParams>('/api/orgs/:orgId/thread-channels', {
      orgId,
    });
    this.http.get<ResBody>(path, { params }).subscribe((channels) => {
      if (!this.data || this.data.userId !== userId) return;

      // Update channels
      let refresh = false;
      channels.forEach((channel) => {
        const changed = this.handleChannel(channel);
        if (changed) refresh = true;
      });

      // Find removed channels
      const oldChannels = this.data.channels;
      const filteredChannels = oldChannels.filter((o) =>
        channels.find((c) => c._id === o._id)
      );

      if (filteredChannels.length !== oldChannels.length) {
        this.data.channels = filteredChannels;
        refresh = true;
      }

      if (refresh) {
        this.sortChannels();
        this.channelListSubject.next(this.data.channels);
      }
      this.setLocalStorageChannelList();
    });
  }

  handleChannel(channel: Channel): boolean {
    if (!this.data) return false;
    const index = this.data.channels.findIndex(
      ({ _id }) => _id === channel._id
    );
    if (index === -1) {
      if (!channel.closedAt) {
        this.data.channels.push({
          _id: channel._id,
          title: channel.title,
          userId: this.data.userId,
          mode: channel.mode,
        });
        return true;
      } else {
        return false;
      }
    } else {
      if (channel.closedAt) {
        this.data.channels.splice(index, 1);
        return true;
      } else {
        if (this.data.channels[index].title !== channel.title) {
          this.data.channels[index] = {
            _id: channel._id,
            title: channel.title,
            mode: channel.mode,
            userId: this.data.userId,
          };
          return true;
        } else {
          return false;
        }
      }
    }
  }

  sortChannels() {
    if (this.data)
      this.data.channels = this.data.channels.sort((a, b) =>
        sortCaseInsensitive(a.title, b.title)
      );
  }

  private getLocalStorageChannelList() {
    if (!this.data) return undefined;

    const entry = localStorage.getItem('channels.' + this.data.userId);
    if (!entry) return undefined;
    return JSON.parse(entry);
  }

  private setLocalStorageChannelList() {
    if (!this.data) return undefined;
    const { userId, channels } = this.data;

    return localStorage.setItem('channels.' + userId, JSON.stringify(channels));
  }
}
